import { isWebView } from '@/utils/webview'

/** Expiry is only a scheduling hint; the server verifies every credential. */
export function tokenExpiresAt(token: string): number | null {
  try {
    const value = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp
    return typeof value === 'number' && Number.isFinite(value) ? value * 1000 : null
  } catch { return null }
}

let inFlight: Promise<string | null> | null = null
let generation = 0
if (typeof window !== 'undefined') {
  window.addEventListener('auth:unauthorized', () => { generation++; inFlight = null })
}

export function getSessionToken(forceRefresh = false): Promise<string | null> {
  if (inFlight) return inFlight
  const started = generation
  const pending = readSessionToken(forceRefresh).then(token => {
    if (generation !== started) throw new Error('Session changed during token acquisition')
    return token
  }).finally(() => { if (inFlight === pending) inFlight = null })
  inFlight = pending
  return pending
}

async function readSessionToken(forceRefresh: boolean): Promise<string | null> {
  const session = (window as unknown as { Clerk?: { session?: { getToken: (options?: { skipCache: boolean }) => Promise<string | null> } } }).Clerk?.session
  if (!isWebView() && (session || window.__getClerkToken)) {
    // A failed active provider must not silently authenticate a different user.
    const token = session ? await session.getToken(forceRefresh ? { skipCache: true } : undefined) : await window.__getClerkToken!()
    if (token && session && !forceRefresh && (tokenExpiresAt(token) ?? Infinity) < Date.now() + 15_000) {
      return session.getToken({ skipCache: true })
    }
    return token
  }
  const token = localStorage.getItem('ACCESS_TOKEN')
  if (localStorage.getItem('REFRESH_TOKEN') && (forceRefresh || (token && (tokenExpiresAt(token) ?? Infinity) < Date.now() + 15_000))) {
    const { authApi } = await import('@/api/auth')
    try {
      return await authApi.refreshToken()
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 401) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { reason: 'refresh_rejected' } }))
      }
      throw error
    }
  }
  return token
}
